import type { BulkIssue, BulkPreview, BulkPreviewProduct } from "../api/types";
import { ACTION_LABELS, familyLabel, issueLocation, stockSentence } from "./labels";

/**
 * What the workbook would do, before it does any of it.
 *
 * DELIBERATELY NOT A SPREADSHEET. The workbook is the editing surface and it is
 * better at being one than anything that could be built here; rebuilding it in
 * the browser would produce a second place to make an edit and two answers
 * about what the file says. This is a decision screen: what would change, what
 * is wrong, and whether to go ahead.
 *
 * THE STOCK SENTENCE IS NOT DECORATION. An operator about to rewrite a
 * catalogue is being asked to trust that it does not rewrite their shelves, and
 * "existing stock is not changed" is the one thing they cannot verify by
 * reading the file. It is said once at the top, and per product wherever
 * something new would be opened.
 */
export function ImportPreview({
  preview,
  filename,
}: {
  preview: BulkPreview;
  filename: string;
}) {
  return (
    <div className="stack" data-testid="import-preview">
      <div className="bulk__summary" role="status">
        <Figure label="Products" value={preview.products} />
        <Figure label="Create" value={preview.creates} />
        <Figure label="Update" value={preview.updates} />
        <Figure label="No change" value={preview.unchanged} />
        <Figure label="Errors" value={preview.errors} tone="bad" />
        <Figure label="Warnings" value={preview.warnings} tone="warn" />
      </div>

      <p className="bulk__assurance" data-testid="stock-assurance">
        <strong>Existing stock will not be changed by this import.</strong>{" "}
        Quantities and low-stock thresholds are moved in Inventory, which
        records why. A workbook that tries to change one is refused above.
      </p>

      <p className="muted">
        Everything imported stays a <strong>draft</strong>, without
        photographs. Add each product&apos;s images on its own Edit screen, then
        review and publish in Product Management, which validates a product
        before customers see it.
      </p>

      {preview.issues.length > 0 ? (
        <IssueList issues={preview.issues} caption={`Problems with ${filename}`} />
      ) : null}

      <div className="bulk__scroll">
        <table className="table">
          <caption className="sr-only">
            What {filename} would do, product by product
          </caption>
          <thead>
            <tr>
              <th scope="col">Row group</th>
              <th scope="col">Product</th>
              <th scope="col">Family</th>
              <th scope="col" className="num">
                Variants
              </th>
              <th scope="col">Action</th>
              <th scope="col">Stock</th>
            </tr>
          </thead>
          <tbody>
            {preview.items.map((item) => (
              <ProductRow key={item.product_ref} item={item} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Figure({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "bad" | "warn";
}) {
  const className =
    tone && value > 0 ? `bulk__figure bulk__figure--${tone}` : "bulk__figure";
  return (
    <div className={className}>
      <span className="bulk__figureValue num">{value}</span>
      <span className="bulk__figureLabel">{label}</span>
    </div>
  );
}

function ProductRow({ item }: { item: BulkPreviewProduct }) {
  const badge = item.blocked
    ? "badge badge--out_of_stock"
    : item.action === "unchanged"
      ? "badge"
      : "badge badge--in_stock";
  return (
    <>
      <tr className={item.blocked ? "bulk__row is-blocked" : "bulk__row"}>
        <td className="muted">{item.product_ref}</td>
        <td>
          <span className="bulk__product">{item.name ?? "—"}</span>
          <span className="bulk__code">{item.product_code ?? "No code"}</span>
        </td>
        <td className="muted">{familyLabel(item.family)}</td>
        <td className="num">{item.variant_count}</td>
        <td>
          <span className={badge}>
            {item.blocked ? "Error" : ACTION_LABELS[item.action]}
          </span>
          {item.uses_existing_draft && !item.blocked ? (
            <span className="bulk__note">Updates the open draft</span>
          ) : null}
        </td>
        <td className="muted">{stockSentence(item)}</td>
      </tr>
      {item.issues.length > 0 ? (
        <tr className="bulk__issueRow">
          <td colSpan={6}>
            <IssueList
              issues={item.issues}
              caption={`Problems with ${item.product_ref}`}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

export function IssueList({
  issues,
  caption,
}: {
  issues: BulkIssue[];
  caption: string;
}) {
  return (
    <ul className="bulk__issues" aria-label={caption}>
      {issues.map((issue, index) => (
        <li
          key={`${issue.code}-${issue.row ?? "x"}-${index}`}
          className={
            issue.severity === "error"
              ? "bulk__issue is-error"
              : "bulk__issue is-warning"
          }
        >
          <span className="bulk__issueSeverity">
            {issue.severity === "error" ? "Error" : "Warning"}
          </span>
          {issueLocation(issue) ? (
            <span className="bulk__issueWhere">{issueLocation(issue)}</span>
          ) : null}
          <span className="bulk__issueMessage">{issue.message}</span>
        </li>
      ))}
    </ul>
  );
}
